import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import GameShell from "../../components/Kids/GameShell";
import EmptyState from "../../components/Kids/EmptyState";
import Skeleton from "../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

const LEADERBOARD_BG = "linear-gradient(160deg, #FFF8F2 0%, #FFE9D6 45%, #FFD9BF 100%)";

// Medal gradients + podium bar heights measured from the design reference.
const PODIUM_CONFIG = [
  { place: 2, emoji: "🥈", medal: "linear-gradient(150deg,#D6DCE4,#94A3B8)", bar: "linear-gradient(180deg,#D6DCE4,#94A3B8)", h: "h-24", label: "2nd" },
  { place: 1, emoji: "🥇", medal: "linear-gradient(150deg,#FFD86B,#F5A623)", bar: "linear-gradient(180deg,#FFD86B,#F5A623)", h: "h-32", label: "1st" },
  { place: 3, emoji: "🥉", medal: "linear-gradient(150deg,#FFC48A,#F59E4E)", bar: "linear-gradient(180deg,#FFC48A,#F59E4E)", h: "h-20", label: "3rd" },
];

export default function KidsLeaderboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const childId  = params.get("childId") || sessionStorage.getItem("childId");

  const [mode, setMode] = useState("individual"); // "individual" | "schools"
  const [leaders, setLeaders] = useState([]);
  const [schoolLeaders, setSchoolLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const loadLeaders = useCallback(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API}/api/kids/leaderboard`, {
      params:  { limit: 20 },
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then(r => setLeaders(r.data?.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const loadSchoolLeaders = useCallback(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API}/api/kids/leaderboard/schools`, {
      params:  { limit: 20 },
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then(r => setSchoolLeaders(r.data?.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (mode === "individual") loadLeaders(); else loadSchoolLeaders();
  }, [mode, loadLeaders, loadSchoolLeaders]);

  const top3    = leaders.slice(0, 3);
  const rest    = leaders.slice(3);
  // Reorder top3 for podium display: 2nd, 1st, 3rd
  const podium  = top3.length === 3 ? [top3[1], top3[0], top3[2]] : [];

  return (
    <GameShell
      title={t("kids.leaderboard.title")}
      emoji="🏆"
      theme="light"
      bg={LEADERBOARD_BG}
      onBack={() => navigate(`/kids/games?childId=${childId}`)}
      maxWidth={640}
      showControls
    >
      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .float{animation:float 3s ease-in-out infinite}
      `}</style>

      {/* Individual vs School competition toggle */}
      <div className="flex gap-1.5 rounded-2xl p-1.5 mb-4" style={{ background: "#F6E7D8" }}>
        <button onClick={() => setMode("individual")}
          className="flex-1 text-center py-2.5 rounded-xl font-display font-semibold text-sm transition"
          style={mode === "individual"
            ? { background: "#EE5F2B", color: "#fff", boxShadow: "0 4px 10px rgba(238,95,43,.3)" }
            : { color: "#9C8262" }}>
          🏅 Kids
        </button>
        <button onClick={() => setMode("schools")}
          className="flex-1 text-center py-2.5 rounded-xl font-display font-semibold text-sm transition"
          style={mode === "schools"
            ? { background: "#EE5F2B", color: "#fff", boxShadow: "0 4px 10px rgba(238,95,43,.3)" }
            : { color: "#9C8262" }}>
          🏫 Schools
        </button>
      </div>

      {mode === "schools" ? (
        loading ? (
          <LeaderboardSkeleton />
        ) : schoolLeaders.length === 0 ? (
          <EmptyState
            mood="thinking"
            theme="light"
            title="No schools ranked yet"
            subtitle="Add your school in the parent dashboard so your child's games count toward it!"
          />
        ) : (
          <div className="space-y-2">
            {schoolLeaders.map((s) => (
              <div key={s.rank} className="flex items-center gap-3 px-4 py-3 rounded-card shadow-card bg-white border border-cardline">
                <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-display font-semibold text-sm bg-cream text-muted">
                  {s.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body font-extrabold text-sm truncate text-ink">🏫 {s.school}</p>
                  <p className="text-xs text-faint">{s.student_count} student{s.student_count === 1 ? "" : "s"} · avg {s.avg_xp} XP</p>
                </div>
                <div className="text-end shrink-0">
                  <p className="font-display font-semibold text-sm text-brand">{s.total_xp}</p>
                  <p className="text-[10px] text-faint">XP</p>
                </div>
              </div>
            ))}
          </div>
        )
      ) : loading ? (
        <LeaderboardSkeleton />
      ) : error ? (
        <EmptyState
          mood="wrong"
          theme="light"
          title={t("kids.leaderboard.offline")}
          subtitle={t("kids.leaderboard.offlineDesc")}
          cta={t("kids.leaderboard.retry")}
          onCta={loadLeaders}
        />
      ) : leaders.length === 0 ? (
        <EmptyState
          mood="thinking"
          theme="light"
          title={t("kids.leaderboard.noRankings")}
          subtitle={t("kids.leaderboard.noRankingsDesc")}
          cta={t("kids.leaderboard.playAGame")}
          onCta={() => navigate(`/kids/games?childId=${childId}`)}
        />
      ) : (
        <>
          {/* Podium */}
          {podium.length === 3 && (
            <div className="flex items-end justify-center gap-3 mt-4 mb-8">
              {podium.map((kid, pi) => {
                const cfg = PODIUM_CONFIG[pi];
                const isMe = String(kid.child_id) === String(childId);
                return (
                  <div key={cfg.place} className="flex flex-col items-center" style={{ width: cfg.place === 1 ? 120 : 100 }}>
                    {/* Avatar */}
                    <div className={`relative mb-2 ${cfg.place === 1 ? "float" : ""}`}>
                      <div
                        className={`w-16 h-16 rounded-full flex items-center justify-center shadow-card ${isMe ? "ring-4 ring-brand" : ""}`}
                        style={{ background: cfg.medal }}
                      >
                        <span className="text-3xl">{cfg.emoji}</span>
                      </div>
                      {cfg.place === 1 && (
                        <span className="absolute -top-3 start-1/2 -translate-x-1/2 text-2xl">👑</span>
                      )}
                    </div>
                    {/* Name */}
                    <p className={`font-body font-extrabold text-sm text-center truncate w-full ${isMe ? "text-brand" : "text-ink"}`}>
                      {isMe ? t("kids.leaderboard.you") : kid.masked_name || kid.name || "Player"}
                    </p>
                    {/* Podium bar */}
                    <div
                      className={`w-full ${cfg.h} rounded-t-card mt-2 flex flex-col items-center justify-center shadow-card`}
                      style={{ background: cfg.bar }}
                    >
                      <span className="text-white text-2xl font-display font-semibold">{cfg.label}</span>
                      <span className="text-white/80 text-xs font-body font-extrabold">{kid.total_xp || 0} XP</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Rest of leaderboard */}
          <div className="space-y-2">
            {rest.map((kid, i) => {
              const rank = i + 4;
              const isMe = String(kid.child_id) === String(childId);
              return (
                <div key={kid.child_id || i}
                  className={`flex items-center gap-3 px-4 py-3 rounded-card shadow-card transition ${
                    isMe
                      ? "bg-brand/10 border-2 border-brand"
                      : "bg-white border border-cardline"
                  }`}>
                  {/* Rank */}
                  <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-display font-semibold text-sm ${
                    isMe ? "bg-brand/20 text-brand" : "bg-cream text-muted"
                  }`}>
                    {rank}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-body font-extrabold text-sm truncate ${isMe ? "text-brand" : "text-ink"}`}>
                      {isMe ? t("kids.leaderboard.you") : kid.masked_name || kid.name || "Player"}
                    </p>
                    <p className="text-xs text-faint">
                      Level {kid.level || 1} · {kid.badges_count || 0} badges
                    </p>
                  </div>
                  {/* XP */}
                  <div className="text-end shrink-0">
                    <p className="font-display font-semibold text-sm text-brand">{kid.total_xp || 0}</p>
                    <p className="text-[10px] text-faint">XP</p>
                  </div>
                  {isMe && (
                    <span className="text-[10px] bg-brand text-white px-2.5 py-1 rounded-full font-body font-extrabold shrink-0">
                      YOU
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* If top3 only, show message for rest */}
          {rest.length === 0 && leaders.length > 0 && (
            <p className="text-center text-faint text-sm mt-4 font-body font-semibold">
              Play more games to climb the ranks! 🚀
            </p>
          )}
        </>
      )}
    </GameShell>
  );
}

/* ── Loading skeleton: podium + list rows shaped like the real layout ── */
function LeaderboardSkeleton() {
  return (
    <div>
      <div className="flex items-end justify-center gap-3 mt-4 mb-8">
        <div className="flex flex-col items-center gap-2" style={{ width: 100 }}>
          <Skeleton className="w-16 h-16" rounded="9999px" />
          <Skeleton className="h-24 w-full" rounded="16px 16px 0 0" />
        </div>
        <div className="flex flex-col items-center gap-2" style={{ width: 120 }}>
          <Skeleton className="w-16 h-16" rounded="9999px" />
          <Skeleton className="h-32 w-full" rounded="16px 16px 0 0" />
        </div>
        <div className="flex flex-col items-center gap-2" style={{ width: 100 }}>
          <Skeleton className="w-16 h-16" rounded="9999px" />
          <Skeleton className="h-20 w-full" rounded="16px 16px 0 0" />
        </div>
      </div>
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[60px] w-full" rounded="16px" />
        ))}
      </div>
    </div>
  );
}
